import { lastDayOfMonth, startOfMonth, endOfMonth, startOfWeek, endOfWeek, startOfYear, endOfYear, subMonths, subWeeks, subYears } from "date-fns";
import { ChevronDown, ChevronUp } from "lucide-react";
import { useState, useEffect, useMemo } from "react";
import useReportsStore from "~/store/reportsSectionStore";

export default function DefaultRateFilter() {
  const [open, setOpen] = useState(false);
  const { filters, setFilters } = useReportsStore();
  const now = useMemo(() => new Date(), []);
  const currentYear = useMemo(() => new Date().getFullYear(), []);
  const [baseMonth, setBaseMonth] = useState(new Date().getMonth());
  const [compareMonth, setCompareMonth] = useState((new Date().getMonth() + 11) % 12);
  const [baseYear, setBaseYear] = useState(currentYear);
  const [compareYear, setCompareYear] = useState(currentYear);
  const monthOptions = useMemo(
    () =>
      Array.from({ length: 12 }, (_, monthIndex) => ({
        value: monthIndex,
        label: new Intl.DateTimeFormat("en-US", { month: "short" }).format(
          new Date(2020, monthIndex, 1)
        ),
      })),
    []
  );
  const yearOptions = useMemo(
    () =>
      Array.from({ length: 6 }, (_, idx) => {
        const year = currentYear - idx;
        return { value: year, label: String(year) };
      }),
    [currentYear]
  );

  const buildMonthVsMonthPeriod = (
    baseMonthIndex,
    compareMonthIndex,
    baseYearValue,
    compareYearValue
  ) => {
    const baseStart = new Date(baseYearValue, baseMonthIndex, 1);
    const baseEnd = lastDayOfMonth(baseStart);
    const compareStart = new Date(compareYearValue, compareMonthIndex, 1);
    const compareEnd = lastDayOfMonth(compareStart);
    return {
      label: "Month vs Month",
      value: "month_vs_month",
      from: baseStart,
      to: baseEnd,
      compare_from: compareStart,
      compare_to: compareEnd,
    };
  };

  const defaultRateOptions = useMemo(
    () => [
      {
        label: "Last Week",
        value: "last_week",
        from: startOfWeek(subWeeks(now, 1), { weekStartsOn: 0 }),
        to: endOfWeek(subWeeks(now, 1), { weekStartsOn: 0 }),
      },
      {
        label: "Last Month",
        value: "last_month",
        from: startOfMonth(subMonths(now, 1)),
        to: endOfMonth(subMonths(now, 1)),
      },
      {
        label: "Last 3 Months",
        value: "last_3_months",
        from: startOfMonth(subMonths(now, 3)),
        to: endOfMonth(now),
      },
      {
        label: "Last Year",
        value: "last_year",
        from: startOfYear(subYears(now, 1)),
        to: endOfYear(subYears(now, 1)),
      },
      {
        label: "This Year",
        value: "this_year",
        from: startOfYear(now),
        to: now,
      },
      buildMonthVsMonthPeriod(baseMonth, compareMonth, baseYear, compareYear),
    ],
    [now, baseMonth, compareMonth, baseYear, compareYear]
  );

  const defaultOption =
    defaultRateOptions.find((option) => option.label === "This Year") ||
    defaultRateOptions[0];
  const [selected, setSelected] = useState(filters.defaultRateFilter || defaultOption);

  useEffect(() => {
    if (!filters.defaultRateFilter) {
      setFilters({
        ...filters,
        defaultRateFilter: defaultOption
      });
    }
  }, []);

  return (
    <div className="flex flex-row justify-start items-start gap-2">
      {open && (
        <div
          onClick={() => setOpen(false)}
          className="fixed top-0 left-0 w-full h-full bg-transparent z-1"
        ></div>
      )}
      <div
        className={`relative flex flex-col gap-y-2 text-xs bg-zinc-100 rounded p-1 min-h-10 min-w-48 z-10 border ${
          open ? "border-primary shadow-lg" : "border-transparent"
        }`}
      >
        <button
          type="button"
          onClick={() => setOpen(!open)}
          className="flex flex-row justify-between items-center gap-x-2 text-xs cursor-pointer p-1 text-left h-10"
        >
          <div className="flex flex-col">
            <span>Period</span>
            <strong className="text-sm">{selected?.label}</strong>
          </div>
          {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </button>
        {open && (
          <div className="absolute left-0 top-full mt-1 w-full flex flex-col justify-start items-start gap-1 py-1 border border-zinc-300 text-zinc-500 bg-white rounded shadow-lg max-h-32 overflow-y-auto">
            {defaultRateOptions.map((option, index) => (
              <button
                key={index}
                type="button"
                onClick={() => {
                  const nextOption =
                    option?.label === "Month vs Month"
                      ? buildMonthVsMonthPeriod(
                          baseMonth,
                          compareMonth,
                          baseYear,
                          compareYear
                        )
                      : option;
                  setFilters({
                    ...filters,
                    defaultRateFilter: nextOption,
                  });
                  setOpen(false);
                  setSelected(nextOption);
                }}
                className="w-full text-left p-1 hover:text-primary hover:bg-zinc-200 rounded-sm cursor-pointer"
              >
                {option?.label}
              </button>
            ))}
          </div>
        )}
      </div>
      {selected?.label === "Month vs Month" && (
        <div className="flex flex-row items-start gap-2 text-xs">
          <div className="flex flex-col shrink-0">
            <span className="text-[11px] text-zinc-600">Base Month</span>
            <select
              className="h-10 min-w-[104px] border border-zinc-200 rounded-sm bg-white px-2"
              value={baseMonth}
              onChange={(event) => {
                const nextBaseMonth = Number(event.target.value);
                setBaseMonth(nextBaseMonth);
                const nextPeriod = buildMonthVsMonthPeriod(
                  nextBaseMonth,
                  compareMonth,
                  baseYear,
                  compareYear
                );
                setFilters({
                  ...filters,
                  defaultRateFilter: nextPeriod,
                });
                setSelected(nextPeriod);
              }}
            >
              {monthOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col shrink-0">
            <span className="text-[11px] text-zinc-600">Base Year</span>
            <select
              className="h-10 min-w-[96px] border border-zinc-200 rounded-sm bg-white px-2"
              value={baseYear}
              onChange={(event) => {
                const nextBaseYear = Number(event.target.value);
                setBaseYear(nextBaseYear);
                const nextPeriod = buildMonthVsMonthPeriod(
                  baseMonth,
                  compareMonth,
                  nextBaseYear,
                  compareYear
                );
                setFilters({
                  ...filters,
                  defaultRateFilter: nextPeriod,
                });
                setSelected(nextPeriod);
              }}
            >
              {yearOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col shrink-0">
            <span className="text-[11px] text-zinc-600">Compare Month</span>
            <select
              className="h-10 min-w-[128px] border border-zinc-200 rounded-sm bg-white px-2"
              value={compareMonth}
              onChange={(event) => {
                const nextCompareMonth = Number(event.target.value);
                setCompareMonth(nextCompareMonth);
                const nextPeriod = buildMonthVsMonthPeriod(
                  baseMonth,
                  nextCompareMonth,
                  baseYear,
                  compareYear
                );
                setFilters({
                  ...filters,
                  defaultRateFilter: nextPeriod,
                });
                setSelected(nextPeriod);
              }}
            >
              {monthOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col shrink-0">
            <span className="text-[11px] text-zinc-600">Compare Year</span>
            <select
              className="h-10 min-w-[96px] border border-zinc-200 rounded-sm bg-white px-2"
              value={compareYear}
              onChange={(event) => {
                const nextCompareYear = Number(event.target.value);
                setCompareYear(nextCompareYear);
                const nextPeriod = buildMonthVsMonthPeriod(
                  baseMonth,
                  compareMonth,
                  baseYear,
                  nextCompareYear
                );
                setFilters({
                  ...filters,
                  defaultRateFilter: nextPeriod,
                });
                setSelected(nextPeriod);
              }}
            >
              {yearOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}
    </div>
  );
}
